import React, { useState, useEffect } from "react";
import "./MantrasList.css";
import AddCategoryForm from "./AddCategoryForm";
import AddMantraForm from "./AddMantraForm";
import MantraCategoryCard from "./MantraCategoryCard";

const API_BASE = "https://pooja-path-1.onrender.com/api/mantras";

const MantrasList = () => {
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddCategoryForm, setShowAddCategoryForm] = useState(false);
    const [showAddMantraForm, setShowAddMantraForm] = useState(false);
    const [categorySearchTerm, setCategorySearchTerm] = useState("");
    const [mantraSearchTerm, setMantraSearchTerm] = useState("");

    // Edit states
    const [editingCategory, setEditingCategory] = useState(null);
    const [editCategoryName, setEditCategoryName] = useState('');

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_BASE}/categories/`);
            if (!res.ok) throw new Error("Failed to fetch categories");
            const data = await res.json();
            if (Array.isArray(data)) {
                setCategories(data);
            } else if (data && Array.isArray(data.results)) {
                setCategories(data.results);
            } else {
                setCategories([]);
            }
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategoryDetails = async (categoryId) => {
        try {
            const res = await fetch(`${API_BASE}/categories/${categoryId}/`);
            if (!res.ok) throw new Error("Failed to fetch category details");
            const data = await res.json();
            setSelectedCategory(data);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        if (!window.confirm("Delete this category?")) return;
        try {
            const res = await fetch(`${API_BASE}/categories/${categoryId}/delete/`, { method: "DELETE" });
            if (!res.ok) throw new Error("Failed to delete category");
            await fetchCategories();
            if (selectedCategory?.id === categoryId) setSelectedCategory(null);
        } catch (err) {
            alert(err.message);
        }
    };

    const handleEditCategory = (category) => {
        setEditingCategory(category);
        setEditCategoryName(category.name);
    };

    const handleCancelEditCategory = () => {
        setEditingCategory(null);
        setEditCategoryName('');
    };

    const handleSaveEditCategory = async () => {
        try {
            const res = await fetch(`${API_BASE}/categories/${editingCategory.id}/update/`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: editCategoryName })
            });
            if (res.ok) {
                alert('Category updated successfully');
                setEditingCategory(null);
                setEditCategoryName('');
                await fetchCategories();
                if (selectedCategory?.id === editingCategory.id) {
                    setSelectedCategory({ ...selectedCategory, name: editCategoryName });
                }
            } else {
                alert('Failed to update category');
            }
        } catch (err) {
            alert('Error updating category');
        }
    };

    const handleDeleteMantra = async (categoryId, mantraName) => {
        if (!window.confirm("Delete this mantra?")) return;
        try {
            const encoded = encodeURIComponent(mantraName);
            const res = await fetch(`${API_BASE}/categories/${categoryId}/mantras/${encoded}/delete/`, { method: "DELETE" });
            if (!res.ok) throw new Error("Failed to delete mantra");
            fetchCategoryDetails(categoryId);
        } catch (err) {
            alert(err.message);
        }
    };

    const handleEditMantra = async (categoryId, oldName, newName, newText) => {
        try {
            const encoded = encodeURIComponent(oldName);
            const res = await fetch(`${API_BASE}/categories/${categoryId}/mantras/${encoded}/update/`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: newName, mantra_text: newText })
            });
            if (res.ok) {
                alert('Mantra updated successfully');
                await fetchCategoryDetails(categoryId);
            } else {
                alert('Failed to update mantra');
            }
        } catch (err) {
            alert('Error updating mantra');
        }
    };

    const filteredCategories = categories.filter((c) =>
        c.name.toLowerCase().includes(categorySearchTerm.toLowerCase())
    );

    const filteredMantras = selectedCategory?.mantras?.filter((m) =>
        m.name.toLowerCase().includes(mantraSearchTerm.toLowerCase()) ||
        m.mantra_text.toLowerCase().includes(mantraSearchTerm.toLowerCase())
    ) || [];

    if (loading) return <div className="mantras-list"><div className="loading-message">Loading categories...</div></div>;

    return (
        <div className="mantras-list">
            {/* Edit Category Modal */}
            {editingCategory && (
                <div className="edit-modal-overlay">
                    <div className="edit-modal">
                        <h2>✏️ Edit Category</h2>
                        <div className="edit-form">
                            <label>Category Name:</label>
                            <input
                                type="text"
                                value={editCategoryName}
                                onChange={(e) => setEditCategoryName(e.target.value)}
                            />
                            <div className="edit-modal-buttons">
                                <button className="save-button" onClick={handleSaveEditCategory}>💾 Save</button>
                                <button className="cancel-button" onClick={handleCancelEditCategory}>❌ Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="list-header">
                <div>
                    <h2 className="section-title">📿 Mantras Management</h2>
                    <p className="section-description">Explore our collection of sacred mantras for inner peace & spiritual guidance</p>
                </div>
                <button className="add-button" onClick={() => setShowAddCategoryForm(true)}>➕ Add Category</button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="mantras-layout">
                <div className="categories-section">
                    <h3>Categories ({categories.length})</h3>
                    <div className="search-bar-container">
                        <input
                            type="text"
                            placeholder="🔍 Search categories..."
                            value={categorySearchTerm}
                            onChange={(e) => setCategorySearchTerm(e.target.value)}
                            className="search-input"
                        />
                    </div>

                    <div className="categories-grid">
                        {filteredCategories.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-icon">📂</div>
                                <h3>No categories found</h3>
                            </div>
                        ) : (
                            filteredCategories.map((category) => (
                                <div
                                    key={category.id}
                                    className={`category-card ${selectedCategory?.id === category.id ? "active" : ""}`}
                                    onClick={() => fetchCategoryDetails(category.id)}
                                >
                                    <div className="category-header">
                                        <h4>{category.name}</h4>
                                        <div className="category-actions">
                                            <button className="edit-btn" onClick={(e) => { e.stopPropagation(); handleEditCategory(category); }} title="Edit category">✏️</button>
                                            <button className="delete-btn" onClick={(e) => { e.stopPropagation(); handleDeleteCategory(category.id); }}>🗑️</button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {selectedCategory && (
                    <div className="category-details">
                        <div className="details-header">
                            <h3>{selectedCategory.name}</h3>
                            <button className="add-mantras-btn" onClick={() => setShowAddMantraForm(true)}>➕ Add Mantra</button>
                        </div>

                        <h4>Mantras ({selectedCategory.mantras?.length || 0})</h4>
                        <div className="search-bar-container">
                            <input
                                type="text"
                                placeholder="🔍 Search mantras..."
                                value={mantraSearchTerm}
                                onChange={(e) => setMantraSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>

                        <div className="mantras-grid">
                            {filteredMantras.length === 0 ? (
                                <div className="empty-mantras">No mantras found</div>
                            ) : (
                                filteredMantras.map((mantra) => (
                                    <MantraCategoryCard
                                        key={mantra.name}
                                        mantra={mantra}
                                        categoryId={selectedCategory.id}
                                        onDelete={handleDeleteMantra}
                                        onEdit={handleEditMantra}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>

            {showAddCategoryForm && (
                <AddCategoryForm onClose={() => setShowAddCategoryForm(false)} onCategoryAdded={fetchCategories} />
            )}

            {showAddMantraForm && selectedCategory && (
                <AddMantraForm
                    categoryId={selectedCategory.id}
                    categoryName={selectedCategory.name}
                    onClose={() => setShowAddMantraForm(false)}
                    onMantraAdded={() => fetchCategoryDetails(selectedCategory.id)}
                />
            )}
        </div>
    );
};

export default MantrasList;
